import * as React from 'react';

import type { ContentBranchHistoryItem, Revision } from '@gitbook/hive-core';
import { getSuspense } from '@gitbook/util-suspense-cache';

import { useHiveClient } from './useHiveClient';

interface HiveBranchHistoryInput {
    contentId: string;
    branchId: string;
    limit: number;
    initialPage: Revision['id'];
    git?: boolean;
}

/**
 * Get the paginated history of a branch.
 */
export function useHiveBranchHistory(input: HiveBranchHistoryInput): {
    history: ContentBranchHistoryItem[];
    loadMore?: () => Promise<void>;
} {
    const { contentId, branchId, limit, initialPage, git = false } = input;

    const client = useHiveClient();

    const [history, setHistory] = React.useState<ContentBranchHistoryItem[]>([]);
    const [nextPage, setNextPage] = React.useState<string | undefined>(initialPage);

    const initialHistory = getSuspense(
        client.store.getBranchHistory(contentId, branchId, {
            page: initialPage,
            limit,
            git,
        })
    );

    const fetchData = React.useCallback(
        async (page, initial: boolean = false) => {
            const data = await client.store.getBranchHistory(contentId, branchId, {
                page,
                limit,
                git,
            });
            if (data) {
                setHistory((prev: ContentBranchHistoryItem[]) => {
                    if (initial) {
                        const index = data.history.findIndex(
                            (item) => item.revision.id === prev[0]?.revision.id
                        );

                        if (index === -1) {
                            return data.history;
                        }

                        return dedupeHistory([...data.history.slice(0, index), ...prev]);
                    }

                    return dedupeHistory([...prev, ...data.history]);
                });
                setNextPage(data.next?.page);
            }
        },
        [contentId, branchId, limit, git]
    );

    React.useEffect(() => {
        fetchData(initialPage, true);
    }, [contentId, branchId, limit, initialPage]);

    const loadMore = React.useCallback(async () => await fetchData(nextPage), [nextPage]);

    return {
        history: history.length ? history : initialHistory.history,
        loadMore: nextPage ? loadMore : undefined,
    };
}

function dedupeHistory(history: ContentBranchHistoryItem[]): ContentBranchHistoryItem[] {
    const seen = new Set<string>();
    return history.filter((item) => {
        const id = item.revision.id;
        if (seen.has(id)) {
            return false;
        }
        seen.add(id);
        return true;
    });
}
